import createClient from "openapi-fetch";
import type { components, paths } from "@trafficplace/api-client";

import { getInitData } from "../../../lib/tg";
import type { ShowcaseView } from "./types";

const MAX_RESOURCE_RETRY_AFTER_SECONDS = 5;

// Resource actions may be rejected at nginx before they reach the API. Retry
// only an explicit, short 429 for idempotent/admission-safe operations. This
// transport wrapper deliberately excludes verify: Showcase owns its bounded,
// server-authorized retry policy and never retries before Retry-After.
export async function fetchResourceAction(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const request = new Request(input, init);
  if (!isResourceAction(request)) return globalThis.fetch(request);

  const retryRequest = request.clone();
  const response = await globalThis.fetch(request);
  const retryAfterSeconds = shortRetryAfterSeconds(response);
  if (retryAfterSeconds === null) return response;

  await new Promise((resolve) =>
    globalThis.setTimeout(resolve, retryAfterSeconds * 1000),
  );
  return globalThis.fetch(retryRequest);
}

function isResourceAction(request: Request): boolean {
  const path = new URL(request.url).pathname;
  return (
    (request.method === "POST" &&
      /\/showcase\/(?:click|delivery-ack)$/.test(path)) ||
    (request.method === "GET" && /\/r\/[^/]+\/resolve$/.test(path))
  );
}

function shortRetryAfterSeconds(response: Response): number | null {
  if (response.status !== 429) return null;
  const value = response.headers.get("Retry-After")?.trim();
  if (!value || !/^\d+$/.test(value)) return null;
  const seconds = Number(value);
  return seconds >= 1 && seconds <= MAX_RESOURCE_RETRY_AFTER_SECONDS
    ? seconds
    : null;
}

// Клиент openapi-fetch с авторизацией через Telegram initData (enduser-flow)
const client = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_URL,
  fetch: fetchResourceAction,
});

// Вспомогательная функция для формирования заголовка авторизации
function authHeaders(): {
  Authorization: string;
  "X-Showcase-Contract": "2";
} {
  return {
    Authorization: `tma ${getInitData()}`,
    "X-Showcase-Contract": "2",
  };
}

function parseShowcaseView(
  body: string,
  contentType: string | null,
): ShowcaseView {
  if (!contentType?.toLowerCase().startsWith("text/html")) {
    // Выключенная/исключённая вставка и старые workers возвращают обычный JSON.
    return { ...JSON.parse(body), system_resource: null };
  }

  const document = new DOMParser().parseFromString(body, "text/html");
  const bootstrap = document.querySelector(
    'script#tp-showcase[type="application/json"]',
  );
  if (!bootstrap?.textContent) throw new Error("showcase_document_invalid");
  const data = JSON.parse(bootstrap.textContent);
  const link = document
    .querySelector<HTMLTemplateElement>("template#tp-system-resource")
    ?.content.querySelector("a");
  if (!link) return { ...data, system_resource: null };

  const name = link.textContent ?? "";
  const subtitle = link.dataset.subtitle ?? "";
  const url = link.getAttribute("href") ?? "";
  const position = Number(link.dataset.position);
  const revision = link.dataset.revision;
  if (
    !name ||
    !url ||
    !Number.isSafeInteger(position) ||
    position < 1 ||
    !revision
  ) {
    throw new Error("showcase_document_invalid");
  }
  return {
    ...data,
    system_resource: { name, subtitle, url, position, revision },
  };
}

// API enduser-режима витрины. После Slice 4 click/verify типизированы через
// сгенерированную OpenAPI-схему.
export const userApi = {
  getShowcase: async (
    flow_token: string,
    launch_context: "showcase" | "resource_auto_open" = "showcase",
    signal?: AbortSignal,
  ) => {
    const result = await client.GET("/showcase", {
      params: { query: { flow_token, launch_context } },
      headers: { ...authHeaders(), Accept: "text/html" },
      signal,
      parseAs: "text",
    });
    return {
      ...result,
      data:
        result.data === undefined
          ? undefined
          : parseShowcaseView(
              result.data,
              result.response.headers.get("Content-Type"),
            ),
    };
  },

  getPreCheck: (flow_token: string, signal?: AbortSignal) =>
    client.GET("/showcase/pre-check", {
      params: { query: { flow_token } },
      headers: authHeaders(),
      signal,
    }),

  click: (body: components["schemas"]["ClickIn"]) =>
    client.POST("/showcase/click", { body, headers: authHeaders() }),

  resolveRedirectToken: (token: string) =>
    client.GET("/r/{token}/resolve", {
      params: { path: { token } },
      headers: authHeaders(),
    }),

  acknowledgeDelivery: (body: components["schemas"]["DeliveryAckIn"]) =>
    client.POST("/showcase/delivery-ack", {
      body,
      headers: authHeaders(),
    }),

  verify: (body: components["schemas"]["VerifyIn"]) =>
    client.POST("/showcase/verify", { body, headers: authHeaders() }),
};
